/**
 * When a thread repeats itself, and when it should stay quiet.
 *
 * Every message used to carry its sender's name and a full "8/23/26, 8:56 AM" stamp. Down a
 * real conversation that is the same two facts restated on every row — the name changes only
 * when the turn changes, and the date at most once a day. Pulling both out of the bubble is
 * most of what separates a chat client from a log viewer.
 *
 * Pure and free of React so the rules can be asserted directly; `Inbox.tsx` only decides how to
 * paint what these return.
 */

/**
 * How long a run of messages from one sender stays visually joined. Past this the name comes
 * back: a reply two hours later reads as a new turn even when nobody else spoke in between.
 */
export const GROUPING_WINDOW_MS = 5 * 60_000

export interface GroupingSubject {
  /** Stable per speaker — direction plus who, or a marker for a private note. */
  senderKey: string
  /** Epoch ms. 0 when unknown, which never groups. */
  at: number
  /** Calendar-day key; a change forces both a separator and the name back. */
  dayKey: string
}

/**
 * Whether this message needs its sender line, given the row above it.
 *
 * `null` for the previous row means the top of the list, or something that isn't a message —
 * a system notice breaks a run, because a "ticket assigned" banner between two of Aisha's
 * messages makes the second read as a fresh turn.
 */
export function shouldShowSender(prev: GroupingSubject | null, curr: GroupingSubject): boolean {
  if (!prev) return true
  if (curr.dayKey !== prev.dayKey) return true
  if (curr.senderKey !== prev.senderKey) return true
  // Unknown timestamps (at === 0) fall through to showing the name rather than silently
  // grouping messages whose order we cannot actually vouch for.
  if (!curr.at || !prev.at) return true
  return curr.at - prev.at > GROUPING_WINDOW_MS
}

export const dayKeyOf = (iso: string): string => new Date(iso).toDateString()

/** "Today" / "Yesterday" / a written date, for the separator between days. */
export function dayLabelOf(iso: string, now: Date = new Date()): string {
  const key = dayKeyOf(iso)
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  if (key === now.toDateString()) return 'Today'
  if (key === yesterday.toDateString()) return 'Yesterday'
  return new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' })
}

/** Clock time only — the day is already named by the separator above. */
export const timeOnly = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleTimeString(undefined, { timeStyle: 'short' }) : '—'
